import { useId, type ReactNode } from "react";
import { useDialogFocus } from "../components/useDialogFocus.ts";

/**
 * A modal dialog for the navy/blue auth/admin screens (controlplane-auth task 11.2, echoing
 * AgentDNA's `Modal`; used by `AddUserModal` etc. in task 13).
 *
 * It reuses the SPA's shared {@link useDialogFocus} hook (design 12.4) so focus management is
 * identical to the existing dialogs: on open focus moves into the panel, Tab/Shift-Tab are trapped
 * inside, Escape closes, and on close focus returns to the trigger. The markup carries the matching
 * roles — a backdrop plus a `role="dialog"` panel with `aria-modal`, `aria-labelledby` (the title)
 * and optional `aria-describedby` (the description) — because roles and focus management are
 * separate concerns and both are needed.
 *
 * It is a controlled component: the caller decides whether it is open and provides `onClose` (called
 * on Escape, backdrop click, and the close button). It renders nothing when closed.
 */

export interface ModalProps {
  /** Whether the modal is shown. When false the component renders nothing. */
  readonly open: boolean;
  /** Called to request a close (Escape, backdrop click, close button). */
  readonly onClose: () => void;
  /** The dialog title; also the accessible name (`aria-labelledby`). */
  readonly title: ReactNode;
  /** An optional description under the title; also the accessible description. */
  readonly description?: ReactNode;
  /** The dialog body. */
  readonly children: ReactNode;
  /** An optional footer region (e.g. action buttons). */
  readonly footer?: ReactNode;
  /** `alertdialog` for a confirmation that needs an explicit choice; defaults to `dialog`. */
  readonly role?: "dialog" | "alertdialog";
}

export function Modal(props: ModalProps): ReactNode {
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);
  const titleId = useId();
  const descId = useId();

  if (!props.open) return null;

  return (
    <div
      className="oa-auth oa-modal-backdrop"
      // A click on the backdrop (outside the panel) requests a close, matching Escape/close-button.
      onClick={(event) => {
        if (event.target === event.currentTarget) props.onClose();
      }}
    >
      <div
        // `useDialogFocus` returns a `RefObject<T | null>`; React 18's DOM `ref` prop is typed as
        // the wider `LegacyRef`. The narrowing cast matches the element type exactly and keeps the
        // new files free of the ref-typing error the older dialogs carry.
        ref={dialogRef as React.Ref<HTMLDivElement>}
        className="oa-modal"
        role={props.role ?? "dialog"}
        aria-modal="true"
        aria-labelledby={titleId}
        {...(props.description === undefined ? {} : { "aria-describedby": descId })}
      >
        <header className="oa-modal__header">
          <h2 className="oa-modal__title" id={titleId}>
            {props.title}
          </h2>
          <button
            type="button"
            className="oa-modal__close"
            aria-label="Close dialog"
            onClick={props.onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        {props.description !== undefined && (
          <p className="oa-modal__description" id={descId}>
            {props.description}
          </p>
        )}
        <div className="oa-modal__body">{props.children}</div>
        {props.footer !== undefined && <footer className="oa-modal__footer">{props.footer}</footer>}
      </div>
    </div>
  );
}
